
import React, { useEffect, useState, useMemo } from 'react';
import Icon from '@ant-design/icons';
import ObjectWidgetProcessor, {
	ObjectWidgetState,
	getDefaultState,
} from '@kemu-io/kemu-core/widgets/object/index.js';
import { WidgetState, WidgetPortContext } from '@kemu-io/kemu-core/types';
import classNames from 'classnames';
import { useDebouncedCallback } from 'use-debounce';
import useReactiveWidgetState from '../../../common/hooks/useReactiveWidgetState';
import {
	GateCustomSettingsProps,
	GetPortsInformationFunction,
	GateUI,
	GateUIProps
} from '../index.ts';
import GateIcon from '../../gateIcon/gateIcon';
import useTranslation from '../../../common/hooks/useTranslation';
import NumericInput from '../../WidgetsComponents/NumericInput/NumericInput';
import { PortLocation } from '../../../types/canvas_t';
import KemuSwitch from '../../form-control/kemuSwitch/KemuSwitch';
import { SETTINGS_CONTAINER_CLASS } from '../../../common/constants';
import styles from './object.module.css';
import { ReactComponent as JsonWidgetIcon } from '@src/assets/img/dataTypes/jsonIcon.svg';


import { getRandomPortName, removeWidgetPortConnections } from '@src/app/recipe/utils';

type Props = GateUIProps


const ObjectWidget = (props: Props): React.JSX.Element => {
	const [state, setState] = useReactiveWidgetState<ObjectWidgetState>(props.recipeId, props.thingRecipeId, props.info.id);

	const fixedState = {
		...getDefaultState(),
		...state
	};

	const t = useTranslation('LogicMaker.Gates.Object');
	const namePlaceholder = t('NamePlaceholder', 'Enter name');

	// Local state for input values to preserve cursor position
	// Keyed by property index
	const [localLabels, setLocalLabels] = useState<Record<number, string>>(() => {
		const initial: Record<number, string> = {};
		fixedState.properties.forEach((property, index) => {
			initial[index] = property.label;
		});
		return initial;
	});

	// Create a stable key to detect external changes to property labels
	const propertiesKey = useMemo(() => {
		return fixedState.properties.map((p, i) => `${i}:${p.label}`).join('|');
	}, [fixedState.properties]);

	// Sync local state when widget state changes externally
	useEffect(() => {
		const updated: Record<number, string> = {};
		fixedState.properties.forEach((property, index) => {
			updated[index] = property.label;
		});
		setLocalLabels(updated);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [propertiesKey]);

	// Debounced update to widget state
	const debouncedUpdateProperty = useDebouncedCallback((index: number, value: string) => {
		const properties = [...fixedState.properties];
		if (properties[index] !== undefined) {
			// IMPORTANT: We create a new object to avoid potentially mutating the state reference in the recipe
			// held internally by `useReactiveWidgetState`.
			properties[index] = {
				...properties[index],
				label: value
			};

			setState({
				...fixedState,
				properties
			});
		}
	}, 300);

	const handleLabelChange = (index: number, value: string) => {
		setLocalLabels(prev => ({
			...prev,
			[index]: value
		}));
		// Update widget state after user stops typing
		debouncedUpdateProperty(index, value);
	};

	return (
		<div className={`${styles.GateBody}`}>
			{fixedState.properties.map((property, i) => (
				<div key={i} className={styles.PropItem}>
					<div className={classNames('gate-input name', { 'has-value': false })}>
						<input
							type='text'
							placeholder={namePlaceholder}
							value={localLabels[i] ?? property.label}
							onChange={evt => handleLabelChange(i, evt.currentTarget.value)}
						/>
					</div>
				</div>
			))}
		</div>
	);
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={JsonWidgetIcon} />}/>
	);
};

const GateCustomSettings = (props: GateCustomSettingsProps): React.JSX.Element => {
	const { recipeId, blockId, gateInfo, redrawPortsAndConnections,  } = props;
	const [state, setState] = useReactiveWidgetState<ObjectWidgetState>(recipeId, blockId, gateInfo.id);
	const fixedState = {
		...getDefaultState(),
		...state
	};

	const t = useTranslation('LogicMaker.Gates.Object.Settings');

	const handleInputChange = (value: number) => {

		let remainingProps = [...fixedState.properties];
		if (value < fixedState.properties.length) {
			// Remove the last property
			const lastProp = remainingProps[remainingProps.length - 1];
			// Remove connections to the given port
			removeWidgetPortConnections(
				recipeId,
				blockId,
				gateInfo.id,
				lastProp.name
			);

			remainingProps = remainingProps.slice(0, value);
		} else if (value > fixedState.properties.length) {
			const label = `${String.fromCharCode(65 + value - 2 + 32)}`;
			const existingNames = fixedState.properties.map(p => p.name);
			const name = getRandomPortName(label, existingNames);
			remainingProps = [
				...remainingProps,
				{
					label,
					name,
				}
			];
		}


		setState({
			...fixedState,
			properties: remainingProps,
		}, true);
	};

	const handleSwitchChange = (checked: boolean) => {
		removeWidgetPortConnections(
			recipeId,
			blockId,
			gateInfo.id,
			'trigger'
		);

		setState({ ...fixedState, useTriggerPort: checked });
	};

	// Redraw ports and connections when the use trigger port or the number of properties changes
	useEffect(() => {
		redrawPortsAndConnections();
	}, [redrawPortsAndConnections, fixedState.useTriggerPort, fixedState.properties.length]);

	return (
		<div className={classNames(styles.SettingsContainer, SETTINGS_CONTAINER_CLASS)}>
			{props.children}
			<div className={styles.InputContainer}>
				<label>{t('Title', 'Properties')}</label>
				<NumericInput
					min={1}
					max={20}
					value={fixedState.properties.length}
					onChange={handleInputChange}
				/>
			</div>
			<div className={styles.SwitchContainer}>
				<label>{t('UseTriggerPort', 'Trigger port')}</label>
				<KemuSwitch
					size="small"
					checked={fixedState.useTriggerPort}
					onChange={handleSwitchChange}
				/>
			</div>
		</div>
	);
};

const getPortsInformation: GetPortsInformationFunction = (state: WidgetState<ObjectWidgetState>, widgetInfo) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = ObjectWidgetProcessor.getOutputNames(state, portContext);
	const inputNames = ObjectWidgetProcessor.getInputNames(state, portContext);

	const portSize = 0.02;
	const topPadding = inputNames.length <= 12 ? 0.15 : 0.05;
	const inputFraction = ((1 - (topPadding + portSize)) / inputNames.length);

	const getPositionFromIndex = (index: number): PortLocation => {
		let space = (inputFraction * index) + topPadding;
		const totalPorts = state.properties.length + (state.useTriggerPort ? 1 : 0);
		if (totalPorts === 1) { space = 0.5; }
		if (totalPorts === 2) { space = 0.30 + (0.4 * index); }
		if (totalPorts === 3) { space = 0.25 + (0.25 * index); }
		if (totalPorts === 4) { space = 0.27 + (0.17 * index); }
		if (totalPorts === 5) { space = 0.24 + (0.14 * index); }
		if (totalPorts === 6) { space = 0.20 + (0.13 * index); }

		return [0, space, -1, 0];
	};

	return {
		outputs: [{
			position: 'Right',
			name: outputNames[0].name,
			label: outputNames[0].label,
			type: outputNames[0].type,
			jsonShape: outputNames[0].jsonShape
		}],

		inputs: inputNames.map((input, i) => {
			return  {
				name: input.name,
				label: input.label,
				type: input.type,
				position: getPositionFromIndex(i)
			};
		})
	};
};

export default {
	getPortsInformation,
	BarIcon: GateBarIcon,
	Element: ObjectWidget,
	CustomSettingsDialog: GateCustomSettings,
	hasTitle: true,
	getWidgetTitle: (intl) => intl.formatMessage({ id: 'LogicMaker.Gates.Object.Title', defaultMessage: 'Object' }),
} as GateUI;
